import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import apiClient, { formatApiError } from "@/lib/apiClient";
import { ArrowLeft, LockKey, LinkBreak, EnvelopeSimple } from "@phosphor-icons/react";

const EMBLEM = "https://static.prod-images.emergentagent.com/jobs/e7aa276a-5de1-4c83-900c-894e1c7268a2/images/df5b7e9d7d3c0712e0fada815b5b10f3496a96f18ade7bc4231629bb9c81c851.jpeg";
const inputCls = "mt-2 w-full h-11 px-3.5 rounded-md border border-slate-300 bg-white text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 focus:outline-none transition";

// Backend 400s for expired / invalid / already-used tokens all mention the reset link.
const isLinkError = (err) => err.response?.status === 400 && /reset link/i.test(String(err.response?.data?.detail || ""));

function LinkRecovery({ onRequest }) {
  return (
    <div className="mt-8 space-y-4" data-testid="reset-link-invalid">
      <div className="flex gap-3 text-sm text-slate-700 bg-amber-50 border border-amber-200 rounded-md px-4 py-3">
        <LinkBreak size={18} weight="bold" className="text-amber-600 shrink-0 mt-0.5" />
        <p>This reset link is no longer valid. Links expire after 1 hour and can only be used once.</p>
      </div>
      <button data-testid="reset-request-new-link" type="button" onClick={onRequest}
        className="w-full h-11 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md font-semibold text-sm flex items-center justify-center gap-2 transition-colors">
        <EnvelopeSimple size={16} weight="bold" /> Send me a new link
      </button>
    </div>
  );
}

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [linkInvalid, setLinkInvalid] = useState(!token);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (pw !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await apiClient.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated — please sign in.");
      navigate("/login");
    } catch (err) {
      if (isLinkError(err)) setLinkInvalid(true);
      else setError(formatApiError(err.response?.data?.detail) || "Could not reset password.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#FAFAFA] grain">
      <div className="w-full max-w-sm" data-testid="reset-password-page">
        <div className="inline-flex bg-black rounded-lg p-2 mb-6"><img src={EMBLEM} alt="Proposal Forensics" className="h-10 w-10 object-contain" /></div>
        <h2 className="font-display font-extrabold text-3xl tracking-tight text-[#0F172A]">Set a new password</h2>
        <p className="text-slate-500 mt-2 text-sm">Choose a strong password for your account.</p>

        {linkInvalid ? (
          <LinkRecovery onRequest={() => navigate("/forgot-password")} />
        ) : (
          <form onSubmit={submit} className="space-y-5 mt-8">
            <div>
              <label className="font-mono-plex text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">New Password</label>
              <input data-testid="reset-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required placeholder="••••••••" className={inputCls} />
            </div>
            <div>
              <label className="font-mono-plex text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Confirm Password</label>
              <input data-testid="reset-confirm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required placeholder="••••••••" className={inputCls} />
            </div>
            {error && <div data-testid="reset-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}
            <button data-testid="reset-submit-btn" type="submit" disabled={loading} className="w-full h-11 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md font-semibold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
              <LockKey size={16} weight="bold" /> {loading ? "Updating…" : "Update password"}
            </button>
          </form>
        )}

        <Link to="/login" data-testid="back-to-login" className="mt-8 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0F172A] transition-colors">
          <ArrowLeft size={15} weight="bold" /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
